"use client";

import { CheckCircle2, Globe2, Loader2, Lock, Pencil, Trash2, UserRound } from "lucide-react";

import { Modal } from "@/components/ds/modal";
import { Button } from "@/components/ui-system";
import type { DashboardDto, UserDto } from "@/lib/api/types";
import type { AuthUser } from "@/modules/auth/types";
import type { HmiDashboardType } from "../hmi/utils/default-document";
import { getDashboardLayoutKind } from "../hmi/utils/layout-kind";

export type DashboardEditorState =
  | { open: false }
  | {
      open: true;
      mode: "create" | "edit";
      dashboardId?: string;
      name: string;
      description: string;
      isGlobal: boolean;
      assignedUserIds: string[];
      userSearch: string;
      dashboardType: HmiDashboardType;
    };

export function formatRoleLabel(role: string | null | undefined): string {
  if (!role) {
    return "User";
  }
  return role
    .split("_")
    .map((part) => (part ? part[0]!.toUpperCase() + part.slice(1) : part))
    .join(" ");
}

export function buildCreateEditorState(): Extract<DashboardEditorState, { open: true }> {
  return {
    open: true,
    mode: "create",
    name: "",
    description: "",
    isGlobal: true,
    assignedUserIds: [],
    userSearch: "",
    dashboardType: "classic",
  };
}

export function buildEditEditorState(dashboard: DashboardDto): Extract<DashboardEditorState, { open: true }> {
  return {
    open: true,
    mode: "edit",
    dashboardId: dashboard.id,
    name: dashboard.name,
    description: dashboard.description ?? "",
    isGlobal: dashboard.is_global,
    assignedUserIds: [...dashboard.assigned_user_ids],
    userSearch: "",
    dashboardType: getDashboardLayoutKind(dashboard.layout) === "hmi_canvas" ? "hmi" : "classic",
  };
}

export function canEditDashboardForUser(user: AuthUser | null, dashboard: DashboardDto): boolean {
  if (!user?.id || !user.role) {
    return false;
  }
  if (user.role === "super_admin") {
    return true;
  }
  return dashboard.creator_user_id === user.id;
}

export function filterAssignableUsers(actor: AuthUser | null, users: UserDto[]): UserDto[] {
  if (!actor?.role || !actor.id) {
    return [];
  }
  if (actor.role === "super_admin") {
    return users;
  }
  if (actor.role === "admin") {
    return users.filter((user) => user.role !== "super_admin");
  }
  if (actor.role === "distributor") {
    return users.filter((user) => user.role === "distributor" || (user.role === "user" && user.distributor_user_id === actor.id));
  }
  if (actor.role === "user") {
    return users.filter((user) => user.role === "user");
  }
  if (actor.role !== "super_admin" && actor.role !== "admin" && actor.role !== "distributor") {
    return users.filter((user) => user.role === actor.role);
  }
  return [];
}

export function DashboardMetaModal({
  state,
  saving,
  error,
  availableUsers,
  onClose,
  onChange,
  onToggleAssignment,
  onSubmit,
}: {
  state: DashboardEditorState;
  saving: boolean;
  error: string | null;
  availableUsers: UserDto[];
  onClose: () => void;
  onChange: (patch: {
    name?: string;
    description?: string;
    isGlobal?: boolean;
    assignedUserIds?: string[];
    userSearch?: string;
    dashboardType?: HmiDashboardType;
  }) => void;
  onToggleAssignment: (userId: string) => void;
  onSubmit: () => void;
}) {
  if (!state.open) {
    return null;
  }

  const title = state.mode === "create" ? "Create dashboard" : "Edit dashboard";
  const description =
    state.mode === "create"
      ? "Create a global dashboard or restrict it to selected users based on your workspace access."
      : "Update dashboard visibility, assignments, and metadata.";

  const searchTerm = state.userSearch.trim().toLowerCase();
  const filteredUsers = availableUsers.filter((user) => {
    if (!searchTerm) {
      return true;
    }
    const haystack = [user.name ?? "", user.email, user.role ?? ""].join(" ").toLowerCase();
    return haystack.includes(searchTerm);
  });

  function optionCardClass(selected: boolean) {
    return selected
      ? "dashboard-option-card dashboard-option-card-selected"
      : "dashboard-option-card";
  }

  return (
    <Modal
      open={state.open}
      onClose={onClose}
      title={title}
      description={description}
      size="lg"
      footer={
        <div className="flex justify-end gap-ds-2">
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" variant="primary" size="sm" onClick={onSubmit} disabled={saving}>
            {saving ? "Saving..." : state.mode === "create" ? "Create dashboard" : "Save changes"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-ds-4 text-caption">
        {error ? (
          <p className="rounded-xl border border-error/30 bg-error/5 px-ds-3 py-ds-2 text-error" role="alert">
            {error}
          </p>
        ) : null}
        <label className="flex flex-col gap-ds-1">
          <span className="font-medium text-muted">Name</span>
          <input
            className="ui-control py-ds-2"
            value={state.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder="Operations dashboard"
          />
        </label>
        <label className="flex flex-col gap-ds-1">
          <span className="font-medium text-muted">Description</span>
          <textarea
            className="ui-control min-h-[7rem] resize-y py-ds-2"
            value={state.description}
            onChange={(e) => onChange({ description: e.target.value })}
            placeholder="Optional description"
          />
        </label>

        {state.mode === "create" ? (
          <div className="flex flex-col gap-ds-2">
            <span className="font-medium text-muted">Dashboard type</span>
            <div className="grid grid-cols-1 gap-ds-2 sm:grid-cols-2">
              <button
                type="button"
                aria-pressed={state.dashboardType === "classic"}
                className={`rounded-2xl px-ds-4 py-ds-3 text-left transition ${optionCardClass(state.dashboardType === "classic")}`}
                onClick={() => onChange({ dashboardType: "classic" })}
              >
                <div className="flex items-start justify-between gap-ds-3">
                  <div>
                    <div className="font-semibold text-foreground">Classic grid</div>
                    <p className="mt-ds-1 text-micro text-muted">Existing widget cards and grid layout.</p>
                  </div>
                  {state.dashboardType === "classic" ? (
                    <span className="dashboard-option-check">
                      <CheckCircle2 className="h-4 w-4" aria-hidden />
                    </span>
                  ) : null}
                </div>
              </button>
              <button
                type="button"
                aria-pressed={state.dashboardType === "hmi"}
                className={`rounded-2xl px-ds-4 py-ds-3 text-left transition ${optionCardClass(state.dashboardType === "hmi")}`}
                onClick={() => onChange({ dashboardType: "hmi" })}
              >
                <div className="flex items-start justify-between gap-ds-3">
                  <div>
                    <div className="font-semibold text-foreground">HMI canvas</div>
                    <p className="mt-ds-1 text-micro text-muted">Freeform industrial view with runtime mode and bindings.</p>
                  </div>
                  {state.dashboardType === "hmi" ? (
                    <span className="dashboard-option-check">
                      <CheckCircle2 className="h-4 w-4" aria-hidden />
                    </span>
                  ) : null}
                </div>
              </button>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-card-border bg-card/35 p-ds-4">
            <p className="font-medium text-foreground">Dashboard type</p>
            <p className="mt-ds-1 text-caption text-muted">
              {state.dashboardType === "hmi" ? "HMI canvas dashboards keep the freeform industrial editor." : "Classic dashboards keep the existing widget grid."}
            </p>
          </div>
        )}

        <div className="flex flex-col gap-ds-2">
          <span className="font-medium text-muted">Visibility</span>
          <div className="grid grid-cols-1 gap-ds-2 sm:grid-cols-2">
            <button
              type="button"
              aria-pressed={state.isGlobal}
              className={`rounded-2xl px-ds-4 py-ds-3 text-left transition ${optionCardClass(state.isGlobal)}`}
              onClick={() => onChange({ isGlobal: true })}
            >
              <div className="flex items-start justify-between gap-ds-3">
                <div>
                  <div className="flex items-center gap-ds-2">
                    <Globe2 className="h-4 w-4 text-emerald-600" aria-hidden />
                    <span className="font-semibold text-foreground">Global dashboard</span>
                  </div>
                  <p className="mt-ds-1 text-micro text-muted">Visible according to your workspace role's sharing rules.</p>
                </div>
                {state.isGlobal ? (
                  <span className="dashboard-option-check">
                    <CheckCircle2 className="h-4 w-4" aria-hidden />
                  </span>
                ) : null}
              </div>
            </button>
            <button
              type="button"
              aria-pressed={!state.isGlobal}
              className={`rounded-2xl px-ds-4 py-ds-3 text-left transition ${optionCardClass(!state.isGlobal)}`}
              onClick={() => onChange({ isGlobal: false })}
            >
              <div className="flex items-start justify-between gap-ds-3">
                <div>
                  <div className="flex items-center gap-ds-2">
                    <Lock className="h-4 w-4 text-amber-600" aria-hidden />
                    <span className="font-semibold text-foreground">Restricted dashboard</span>
                  </div>
                  <p className="mt-ds-1 text-micro text-muted">Visible only to selected users.</p>
                </div>
                {!state.isGlobal ? (
                  <span className="dashboard-option-check">
                    <CheckCircle2 className="h-4 w-4" aria-hidden />
                  </span>
                ) : null}
              </div>
            </button>
          </div>
        </div>

        {!state.isGlobal ? (
          <div className="flex flex-col gap-ds-3 rounded-2xl border border-card-border bg-card/35 p-ds-4">
            <div className="flex flex-wrap items-center justify-between gap-ds-2">
              <div>
                <p className="font-medium text-foreground">Assigned users</p>
                <p className="text-micro text-muted">{state.assignedUserIds.length} selected</p>
              </div>
              <input
                className="ui-control w-full py-ds-2 text-caption sm:w-64"
                value={state.userSearch}
                onChange={(e) => onChange({ userSearch: e.target.value })}
                placeholder="Search users..."
              />
            </div>

            <div className="max-h-72 space-y-ds-2 overflow-y-auto pr-ds-1">
              {filteredUsers.length === 0 ? (
                <p className="rounded-xl border border-dashed border-card-border px-ds-3 py-ds-4 text-center text-micro text-muted">
                  No users match this search.
                </p>
              ) : (
                filteredUsers.map((user) => {
                  const checked = state.assignedUserIds.includes(user.id);
                  return (
                    <label
                      key={user.id}
                      className={`flex cursor-pointer items-start gap-ds-3 rounded-xl border px-ds-3 py-ds-3 transition ${
                        checked
                          ? "border-[color:var(--accent)]/40 bg-[color:var(--accent)]/7"
                          : "border-card-border bg-background/50 hover:border-border-strong"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 rounded border-border-strong"
                        checked={checked}
                        onChange={() => onToggleAssignment(user.id)}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-ds-2">
                          <span className="truncate font-medium text-foreground">{user.name ?? user.email}</span>
                            <span className="dashboard-pill dashboard-pill-assigned">
                              {formatRoleLabel(user.role)}
                            </span>
                        </div>
                        <p className="mt-1 truncate text-micro text-muted">{user.email}</p>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

export function DashboardListCard({
  dashboard,
  deleting,
  canEdit,
  canDelete,
  onOpen,
  onDelete,
}: {
  dashboard: DashboardDto;
  deleting: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onOpen: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="rounded-3xl border border-card-border bg-card/55 p-ds-5 shadow-card transition hover:border-border-strong hover:bg-card/75 hover:shadow-card-hover">
      <div className="flex items-start justify-between gap-ds-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-ds-2">
            <p className="truncate text-body font-semibold text-foreground">{dashboard.name}</p>
            <span className={`dashboard-pill ${dashboard.is_global ? "dashboard-pill-global" : "dashboard-pill-restricted"}`}>
              {dashboard.is_global ? <Globe2 className="h-3 w-3" aria-hidden /> : <Lock className="h-3 w-3" aria-hidden />}
              {dashboard.is_global ? "Global" : "Restricted"}
            </span>
            {!dashboard.is_global ? (
              <span className="dashboard-pill dashboard-pill-assigned normal-case tracking-normal">
                <UserRound className="h-3 w-3" aria-hidden />
                {dashboard.assigned_user_ids.length} assigned
              </span>
            ) : null}
          </div>
          <p className="mt-ds-2 text-caption text-muted">
            {dashboard.description?.trim() || "No description yet."}
          </p>
        </div>
        {(canEdit || canDelete) && (
          <div className="flex items-center gap-ds-1">
            {canEdit ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="!p-2"
                onClick={onOpen}
                aria-label={`Open ${dashboard.name}`}
              >
                <Pencil className="h-4 w-4" aria-hidden />
              </Button>
            ) : null}
            {canDelete ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="!p-2 text-error hover:!text-error"
                disabled={deleting}
                onClick={onDelete}
                aria-label={`Delete ${dashboard.name}`}
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Trash2 className="h-4 w-4" aria-hidden />}
              </Button>
            ) : null}
          </div>
        )}
      </div>

      <div className="mt-ds-4 flex flex-wrap items-center justify-between gap-ds-2">
        <p className="text-micro text-muted">
          Updated {new Date(dashboard.updated_at).toLocaleDateString()}
        </p>
        <Button type="button" variant="secondary" size="sm" className="gap-ds-2" onClick={onOpen}>
          <Pencil className="h-4 w-4" aria-hidden />
          Open canvas
        </Button>
      </div>
    </div>
  );
}
